import { logError } from './rollbar';

/**
 * Custom error class that preserves HTTP response details
 */
export class FetchError extends Error {
  public status?: number;
  public statusText?: string;
  public url: string;
  public headers?: Record<string, string>;

  constructor(message: string, url: string, status?: number, statusText?: string, headers?: Record<string, string>) {
    super(message);
    this.name = 'FetchError';
    this.url = url;
    this.status = status;
    this.statusText = statusText;
    this.headers = headers;
  }
}

/**
 * Options for fetch with retry
 */
export interface FetchWithRetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Initial delay in milliseconds (default: 1000ms = 1s) */
  initialDelay?: number;
  /** Maximum delay in milliseconds (default: 10000ms = 10s) */
  maxDelay?: number;
  /** Backoff multiplier (default: 2 for exponential backoff) */
  backoffMultiplier?: number;
  /** Additional fetch options to pass through */
  fetchOptions?: RequestInit;
  /** Custom function to determine if error is retryable (default: retries on network errors and 5xx) */
  shouldRetry?: (error: Error, response: Response | null, attempt: number) => boolean;
}

/**
 * Extract important headers from Response for debugging
 * Focuses on CORS, caching, and Cloudflare-specific headers
 */
function extractResponseHeaders(response: Response): Record<string, string> {
  const headers: Record<string, string> = {};

  // CORS headers
  const corsHeaders = [
    'access-control-allow-origin',
    'access-control-allow-methods',
    'access-control-allow-headers',
    'access-control-expose-headers',
  ];

  // Caching headers
  const cacheHeaders = [
    'cache-control',
    'etag',
    'expires',
    'last-modified',
    'age',
  ];

  // Cloudflare headers
  const cfHeaders = [
    'cf-cache-status',
    'cf-ray',
    'cf-request-id',
  ];

  // Server/content headers
  const serverHeaders = [
    'content-type',
    'content-length',
    'server',
    'date',
  ];

  // Combine all headers we want to capture
  const headersToCapture = [...corsHeaders, ...cacheHeaders, ...cfHeaders, ...serverHeaders];

  headersToCapture.forEach(headerName => {
    const value = response.headers.get(headerName);
    if (value) {
      headers[headerName] = value;
    }
  });

  return headers;
}

/**
 * Default function to determine if a request should be retried
 */
function defaultShouldRetry(_error: Error, response: Response | null, attempt: number): boolean {
  // Don't retry if we've exhausted attempts
  if (attempt >= 3) return false;

  // Retry on network errors (no response)
  if (!response) return true;

  // Retry on server errors (5xx)
  if (response.status >= 500 && response.status < 600) return true;

  // Retry on specific client errors that might be transient
  if (response.status === 408 || response.status === 429) return true;

  // Don't retry on other errors (4xx like 404, 403, etc.)
  return false;
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch with exponential backoff and retry
 *
 * @param url - The URL to fetch
 * @param options - Retry and fetch options
 * @returns Promise that resolves to the Response
 *
 * @example
 * ```ts
 * const response = await fetchWithRetry('https://api.example.com/data', {
 *   maxRetries: 3,
 *   initialDelay: 1000,
 *   fetchOptions: {
 *     headers: { 'Authorization': 'Bearer token' }
 *   }
 * });
 * const data = await response.json();
 * ```
 */
export async function fetchWithRetry(
  url: string,
  options: FetchWithRetryOptions = {}
): Promise<Response> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffMultiplier = 2,
    fetchOptions = {},
    shouldRetry = defaultShouldRetry,
  } = options;

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[fetchWithRetry] Attempt ${attempt + 1}/${maxRetries + 1} for ${url}`);

      const response = await fetch(url, fetchOptions);

      // If response is OK, return it
      if (response.ok) {
        if (attempt > 0) {
          console.log(`[fetchWithRetry] Success on attempt ${attempt + 1} for ${url}`);
        }
        return response;
      }

      // Create an error for non-OK responses with headers
      const responseHeaders = extractResponseHeaders(response);
      lastError = new FetchError(
        `HTTP ${response.status}: ${response.statusText}`,
        url,
        response.status,
        response.statusText,
        responseHeaders
      );

      // Check if we should retry
      if (!shouldRetry(lastError, response, attempt)) {
        console.log(`[fetchWithRetry] Not retrying (status ${response.status}) for ${url}`);
        throw lastError;
      }

      // Calculate delay with exponential backoff
      if (attempt < maxRetries) {
        const delay = Math.min(initialDelay * Math.pow(backoffMultiplier, attempt), maxDelay);
        console.log(`[fetchWithRetry] Retrying after ${delay}ms (attempt ${attempt + 1} failed with status ${response.status})`);
        await sleep(delay);
      }
    } catch (error) {
      // Wrap network errors in FetchError for consistent error handling
      const originalError = error instanceof Error ? error : new Error(String(error));
      lastError = new FetchError(
        originalError.message,
        url
      );

      // Check if we should retry
      if (!shouldRetry(lastError, null, attempt)) {
        console.log(`[fetchWithRetry] Not retrying (network error) for ${url}`);
        throw lastError;
      }

      // Calculate delay with exponential backoff
      if (attempt < maxRetries) {
        const delay = Math.min(initialDelay * Math.pow(backoffMultiplier, attempt), maxDelay);
        console.log(`[fetchWithRetry] Retrying after ${delay}ms (attempt ${attempt + 1} failed with error: ${lastError.message})`);
        await sleep(delay);
      }
    }
  }

  // If we've exhausted all retries, throw the last error
  if (lastError) {
    logError(`[fetchWithRetry] All retries exhausted for ${url}`, lastError);
    throw lastError;
  }

  // This should never happen, but TypeScript needs it
  throw new Error('Unexpected error in fetchWithRetry');
}
